import { useEffect, useRef, useState } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { SearchIcon } from 'lucide-react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ToggleButton } from 'primereact/togglebutton';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/app/hooks';
import { useLazySearchDonorsQuery } from '@/app/services/donors';
import { NewDonorDialog } from '@/components/common/new-donor-dialog/NewDonorDialog';
import { WithLocalStorage } from '@/components/hoc';
import { Button as UiButton } from '@/components/ui/button';
import {
  Card as UiCard,
  CardContent as UiCardContent,
} from '@/components/ui/card';
import { Checkbox as UiCheckbox } from '@/components/ui/checkbox';
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Dialog as UiDialog,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, lookupSideBar, sideBar } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Donor } from '@/rtk-query.gql';

import { useAdvancedDonorDataSource } from './useAdvancedDonorDataSource';
import { useColumnDefs } from './useColumnDefs';

import styles from './index.module.css';

interface IAdvancedDonorLookupProps {
  className?: string;
  label?: string;
  value?: Donor;
  ownerId?: string;
  onChange?: (payload: Donor) => void;
  disabled?: boolean;
  revisionComment?: string;
  setRevisionComment?: (payload: string) => void;
  revisionNumber?: {
    previousRevision: string;
    currentRevision: string;
  };
  setRevisionNumber?: (payload: {
    previousRevision: string;
    currentRevision: string;
  }) => void;
  tabIndex?: number;
}

const DATA_TABLE_ROWS = 50;
const CACHE_BLOCK_SIZE = 500;
const LOAD_DEBOUNCE_MS = 500;

export const AdvancedDonorLookup = ({
  className,
  value,
  ownerId,
  label,
  onChange,
  disabled,
  revisionComment,
  setRevisionComment,
  revisionNumber,
  setRevisionNumber,
  tabIndex,
}: IAdvancedDonorLookupProps) => {
  const { t } = useTranslation();
  const defaultGridOptions = useDefaultGridOptions();
  const mode = useAppSelector((state) => state.preferences.mode);
  const columnDefs = useColumnDefs();
  const isSearchResetEnabled = useFeatureFlag('DS_SemenSireDonor_Search_Reset');
  const isSpecificViewContextEnabled = useFeatureFlag('Specific_View_Context');
  const isBaseUi = useFeatureFlag('advanced_donor_lookup_prime_to_base_ui');
  const ALL_OPTION = { label: t('common.all'), value: '' };

  const SEARCH_BY_OPTIONS = columnDefs.map(({ headerName, field }) => ({
    label: headerName,
    value: field,
  }));

  const SEARCH_BY_OPTIONS_WITH_ALL = [ALL_OPTION, ...SEARCH_BY_OPTIONS];

  const gridRef = useRef<AgGridReact>(null);
  const dialogRef = useRef(null);
  const [baseDialogContainer, setBaseDialogContainer] =
    useState<HTMLElement | null>(null);

  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [searchTermTemp, setSearchTermTemp] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedSearchBy, setSelectedSearchBy] = useState(ALL_OPTION.value);
  const [selectedDonor, setSelectedDonor] = useState(value);
  const [searchAllDonors, setSearchAllDonors] = useState(false);
  const [inputValue, setInputValue] = useState<string>(value?.name || '');
  const [newDonorDialogVisible, setNewDonorDialogVisible] = useState(false);
  const [filterByRanch, setFilterByRanch] = useState(
    ownerId !== null ? true : false,
  );

  useEffect(() => {
    setInputValue(value?.name || '');
    setSelectedDonor(value);
  }, [value]);

  const [searchDonorsQuery] = useLazySearchDonorsQuery();
  const { dataSource, refetch } = useAdvancedDonorDataSource({
    query: searchDonorsQuery,
    searchBy: selectedSearchBy,
    searchTerm,
    ownerId: filterByRanch ? ownerId : undefined,
  });

  const handleCloseDialog = () => {
    if (isSearchResetEnabled) {
      setSelectedSearchBy(ALL_OPTION.value);
      setSearchTermTemp('');
      setSearchTerm('');
      setFilterByRanch(false);
      gridRef.current?.api?.setFilterModel(null);
    }
    setDialogVisible(false);
  };

  const dialogFooterTemplate = (disabled: boolean) => {
    return (
      <div className={`mt-3 flex w-full justify-between`}>
        <div>
          <Button
            icon="pi pi-plus"
            label={t('common.new')}
            onClick={() => setNewDonorDialogVisible(true)}
            className={styles.newButton}
          />
        </div>
        <div>
          <Button
            label={t('common.cancel')}
            icon="pi pi-times"
            severity="secondary"
            onClick={handleCloseDialog}
          />
          <Button
            label={t('common.select')}
            icon="pi pi-check"
            onClick={() => {
              handleCloseDialog();
              if (selectedDonor) {
                setInputValue(selectedDonor.name);
                onChange?.(selectedDonor);
              }
            }}
            disabled={disabled}
          />
        </div>
      </div>
    );
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement> | undefined) => {
    if (e) {
      e.preventDefault();
    }

    setSearchTerm(searchTermTemp);
  };

  const handleDialogResize = () => {
    if (gridRef.current) {
      gridRef.current.api.sizeColumnsToFit();
    }
  };

  const handleSelectDonor = () => {
    if (selectedDonor) {
      onChange?.(selectedDonor);
      handleCloseDialog();
    }
  };

  const handleSelectionChanged = (e) => {
    const selectedNode = e.api.getSelectedNodes()[0];

    if (selectedNode && selectedNode.data) {
      setSelectedDonor(selectedNode.data);
    } else {
      setSelectedDonor(null);
    }
  };

  const handleOpenDialog = () => {
    setSearchTermTemp(inputValue);
    setSearchTerm(inputValue);
    setDialogVisible(true);
  };

  if (isBaseUi) {
    return (
      <div className="label-input-group col-span-2">
        <div className="grid grid-cols-[auto_min-content_min-content] items-end gap-2">
          <div className="label-input-group">
            <label htmlFor="donor-input">{label}</label>
            <Input
              className={className}
              id="donor-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleOpenDialog();
                }
              }}
              placeholder={t('donor.searchDonor')}
              tabIndex={tabIndex}
              data-skip-enter-advance
              disabled={disabled}
            />
          </div>
          <div className="flex items-end gap-2">
            <UiButton
              size="icon"
              onClick={handleOpenDialog}
              type="button"
              disabled={disabled}
              aria-label={t('donor.searchDonor')}
            >
              <SearchIcon />
            </UiButton>
            {ownerId ? (
              <ToggleButton
                offLabel={t('common.all')}
                onLabel={t('common.all')}
                onIcon="pi pi-check"
                offIcon="pi pi-times"
                checked={searchAllDonors}
                onChange={(e) => setSearchAllDonors(e.value)}
              />
            ) : null}
          </div>
        </div>
        <UiDialog
          open={dialogVisible}
          onOpenChange={(open) => {
            if (!open) handleCloseDialog();
          }}
        >
          <DialogContent
            preflight={false}
            ref={setBaseDialogContainer}
            className="z-[2000] h-[95vh] md:h-[95vh] md:max-w-[75vw]"
            overlayClassName="z-[2000]"
          >
            <DialogHeader className="preflight">
              <DialogTitle>{t('donor.donorLookup')}</DialogTitle>
            </DialogHeader>
            <DialogBody className="min-h-0">
              <div className="flex h-full flex-col">
                <UiCard className="preflight m-2 w-80">
                  <UiCardContent className="px-4 pt-4 pb-2">
                    <form
                      className="flex flex-col gap-3"
                      onSubmit={handleSearch}
                    >
                      <div className="label-input-group">
                        <label>{t('common.searchBy')}</label>
                        <Select
                          value={selectedSearchBy}
                          onChange={(v) => setSelectedSearchBy(v as string)}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue>
                              {SEARCH_BY_OPTIONS_WITH_ALL.find(
                                (o) => o.value === selectedSearchBy,
                              )?.label ?? ALL_OPTION.label}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent
                            alignItemWithTrigger={false}
                            container={baseDialogContainer ?? undefined}
                          >
                            {SEARCH_BY_OPTIONS_WITH_ALL.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="label-input-group">
                        <label>{t('common.search')}</label>
                        <div className="grid grid-cols-[auto_min-content] items-center gap-x-2">
                          <Input
                            value={searchTermTemp}
                            onChange={(e) => setSearchTermTemp(e.target.value)}
                            data-skip-enter-advance
                          />
                          <UiButton
                            size="icon"
                            type="submit"
                            aria-label={t('common.search')}
                          >
                            <SearchIcon />
                          </UiButton>
                        </div>
                      </div>
                      <label className="flex items-center gap-2">
                        <UiCheckbox
                          id="filter-by-ranch"
                          aria-label={t('farm.filterByRelatedRanch')}
                          checked={filterByRanch}
                          onCheckedChange={(checked) =>
                            setFilterByRanch(checked === true)
                          }
                        />
                        <span>{t('farm.filterByRelatedRanch')}</span>
                      </label>
                    </form>
                  </UiCardContent>
                </UiCard>
                <div className="m-2 min-h-0 flex-1">
                  <WithLocalStorage storageKey="donor-lookup">
                    <AgGridReact
                      gridOptions={defaultGridOptions}
                      className={`ag-theme-transova${mode === 'dark' ? '-dark' : ''} h-full`}
                      ref={gridRef}
                      defaultColDef={{
                        ...defaultColDef,
                        filter: true,
                        filterParams: {
                          buttons: ['apply', 'clear'],
                          closeOnApply: true,
                        },
                      }}
                      columnDefs={columnDefs}
                      getRowId={getRowId<Donor>((params) =>
                        String(params.data.id),
                      )}
                      rowModelType="serverSide"
                      rowSelection="single"
                      cacheBlockSize={CACHE_BLOCK_SIZE}
                      blockLoadDebounceMillis={LOAD_DEBOUNCE_MS}
                      pagination={true}
                      paginationAutoPageSize={true}
                      serverSideDatasource={dataSource ?? undefined}
                      paginationPageSize={DATA_TABLE_ROWS}
                      onSelectionChanged={handleSelectionChanged}
                      onCellDoubleClicked={handleSelectDonor}
                      sideBar={
                        isSpecificViewContextEnabled
                          ? lookupSideBar('advanced-donor-lookup')
                          : sideBar
                      }
                    />
                  </WithLocalStorage>
                </div>
              </div>
            </DialogBody>
            <DialogFooter className="preflight justify-between sm:justify-between">
              <UiButton
                type="button"
                variant="outline"
                onClick={() => setNewDonorDialogVisible(true)}
              >
                {t('common.new')}
              </UiButton>
              <div className="flex gap-2">
                <UiButton
                  type="button"
                  variant="outline"
                  onClick={handleCloseDialog}
                >
                  {t('common.cancel')}
                </UiButton>
                <UiButton
                  type="button"
                  disabled={selectedDonor === null}
                  onClick={() => {
                    handleCloseDialog();
                    if (selectedDonor) {
                      setInputValue(selectedDonor.name);
                      onChange?.(selectedDonor);
                    }
                  }}
                >
                  {t('common.select')}
                </UiButton>
              </div>
            </DialogFooter>
          </DialogContent>
        </UiDialog>
        <NewDonorDialog
          visible={newDonorDialogVisible}
          onHide={() => setNewDonorDialogVisible(false)}
          onSave={(newDonor) => {
            setSelectedDonor(newDonor);
            onChange?.(newDonor);
            setNewDonorDialogVisible(false);
            refetch();
          }}
          gridRef={gridRef}
          revisionComment={revisionComment}
          setRevisionComment={setRevisionComment}
          revisionNumber={revisionNumber}
          setRevisionNumber={setRevisionNumber}
        />
      </div>
    );
  }

  return (
    <div className={`label-input-group col-span-2`}>
      <div className="grid grid-cols-[auto_min-content_min-content] items-end gap-2">
        <div className="label-input-group">
          <label htmlFor="donor-input">{label}</label>
          <InputText
            className={className}
            id="donor-input"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleOpenDialog();
              }
            }}
            placeholder={t('donor.searchDonor')}
            tabIndex={tabIndex}
            data-skip-enter-advance
            disabled={disabled}
          />
        </div>
        <div className="flex items-end gap-2">
          <Button
            icon="pi pi-search"
            onClick={handleOpenDialog}
            type="button"
            disabled={disabled}
          />
          {ownerId ? (
            <ToggleButton
              offLabel={t('common.all')}
              onLabel={t('common.all')}
              onIcon="pi pi-check"
              offIcon="pi pi-times"
              checked={searchAllDonors}
              onChange={(e) => setSearchAllDonors(e.value)}
            />
          ) : null}
        </div>
      </div>
      <Dialog
        ref={dialogRef}
        className={styles.dialog}
        header={t('donor.donorLookup')}
        visible={dialogVisible}
        maximizable
        modal
        onHide={handleCloseDialog}
        footer={() => dialogFooterTemplate(selectedDonor === null)}
        onResize={handleDialogResize}
      >
        <Card className={styles.card}>
          <form
            className={`${styles.lookupForm} label-input-column`.trim()}
            onSubmit={handleSearch}
          >
            <div className="label-input-group">
              <label>{t('common.searchBy')}</label>
              <Dropdown
                value={selectedSearchBy}
                options={SEARCH_BY_OPTIONS_WITH_ALL}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => {
                  setSelectedSearchBy(e.target.value);
                }}
              />
            </div>
            <div className="label-input-group">
              <label>{t('common.search')}</label>
              <div className={styles.searchContainer}>
                <InputText
                  value={searchTermTemp}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch(undefined);
                    }
                  }}
                  onChange={(e) => setSearchTermTemp(e.target.value)}
                  data-skip-enter-advance
                />
                <Button
                  icon="pi pi-search"
                  type="button"
                  onClick={() => handleSearch(undefined)}
                />
              </div>
            </div>
            <div className={styles.filterContainer}>
              <Checkbox
                checked={filterByRanch}
                onChange={(e) => setFilterByRanch(e.checked)}
                id="filter-by-ranch"
              />
              <label htmlFor="filter-by-ranch">
                {t('farm.filterByRelatedRanch')}
              </label>
            </div>
          </form>
        </Card>
        <NewDonorDialog
          visible={newDonorDialogVisible}
          onHide={() => setNewDonorDialogVisible(false)}
          onSave={(newDonor) => {
            setSelectedDonor(newDonor);
            onChange?.(newDonor);
            setNewDonorDialogVisible(false);
            refetch();
          }}
          gridRef={gridRef}
          revisionComment={revisionComment}
          setRevisionComment={setRevisionComment}
          revisionNumber={revisionNumber}
          setRevisionNumber={setRevisionNumber}
        />
        <WithLocalStorage storageKey="donor-lookup">
          <AgGridReact
            gridOptions={defaultGridOptions}
            className={`ag-theme-transova${mode === 'dark' ? '-dark' : ''}`}
            ref={gridRef}
            defaultColDef={{
              ...defaultColDef,
              filter: true,
              filterParams: {
                buttons: ['apply', 'clear'],
                closeOnApply: true,
                //suppressAndOrCondition: true,
              },
            }}
            columnDefs={columnDefs}
            getRowId={getRowId<Donor>((params) => String(params.data.id))}
            rowModelType="serverSide"
            rowSelection="single"
            cacheBlockSize={CACHE_BLOCK_SIZE}
            blockLoadDebounceMillis={LOAD_DEBOUNCE_MS}
            pagination={true}
            paginationAutoPageSize={true}
            serverSideDatasource={dataSource ?? undefined}
            paginationPageSize={DATA_TABLE_ROWS}
            onSelectionChanged={handleSelectionChanged}
            onCellDoubleClicked={handleSelectDonor}
            sideBar={
              isSpecificViewContextEnabled
                ? lookupSideBar('advanced-donor-lookup')
                : sideBar
            }
          />
        </WithLocalStorage>
      </Dialog>
    </div>
  );
};
